import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import authService, { 
  User, 
  LoginCredentials, 
  RegisterData, 
  ForgotPasswordData, 
  ResetPasswordData 
} from '../services/authService';

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (data: ForgotPasswordData) => Promise<{ message: string }>;
  resetPassword: (data: ResetPasswordData) => Promise<{ message: string }>;
  resendVerificationEmail: () => Promise<{ message: string }>;
  verifyEmail: (userId: string, hash: string) => Promise<{ message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const queryClient = useQueryClient();

  // Try to get user from localStorage on initial load
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse user from localStorage', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Listen for logout events from API interceptor
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setIsTokenExpired(true);
      queryClient.clear();
      // Reset token expired flag after clearing state
      setTimeout(() => setIsTokenExpired(false), 100);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [queryClient]);

  // Listen for user update events (e.g., from email verification)
  useEffect(() => {
    const handleUserUpdate = (event: CustomEvent) => {
      const updatedUser = event.detail?.user;
      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        // Invalidate user query to refetch on next access
        queryClient.invalidateQueries({ queryKey: ['user'] });
      }
    };

    window.addEventListener('auth:userUpdated', handleUserUpdate as EventListener);
    return () => window.removeEventListener('auth:userUpdated', handleUserUpdate as EventListener);
  }, [queryClient]);

  // Query to get current user
  const { isLoading, data: currentUser, error: currentUserError } = useQuery({
    queryKey: ['user'],
    queryFn: authService.getCurrentUser,
    enabled: !!localStorage.getItem('token') && !user && !isTokenExpired,
    retry: (failureCount, error: any) => {
      // Don't retry on 401/403 errors (authentication issues)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in older versions)
  });

  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
      localStorage.setItem('user', JSON.stringify(currentUser));
      setIsTokenExpired(false);
      
    }

    if (currentUserError) {
      const errorStatus = (currentUserError as any)?.response?.status;
      if (errorStatus === 401 || errorStatus === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsTokenExpired(true);
      }
    }
  }, [currentUser, currentUserError]);


  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      queryClient.setQueryData(['user'], data.user);
      
      // Don't auto-redirect here, let the login page handle it based on verification status
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: authService.register,
    onSuccess: (data) => {
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      queryClient.setQueryData(['user'], data.user);
      
      // Don't auto-redirect here, let the signup page handle it
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      setUser(null);
      setIsTokenExpired(false);
      queryClient.clear();
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: authService.forgotPassword,
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: authService.resetPassword,
  });

  // Resend verification email mutation
  const resendVerificationEmailMutation = useMutation({
    mutationFn: authService.resendVerificationEmail,
  });

  // Verify email mutation
  const verifyEmailMutation = useMutation({
    mutationFn: ({ userId, hash }: { userId: string; hash: string }) => 
      authService.verifyEmail(userId, hash),
    onSuccess: () => {
      // Refresh user data after successful verification
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  const login = useCallback(async (credentials: LoginCredentials) => {
    await loginMutation.mutateAsync(credentials);
  }, [loginMutation]);

  const register = useCallback(async (data: RegisterData) => {
    await registerMutation.mutateAsync(data);
  }, [registerMutation]);

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const forgotPassword = useCallback(async (data: ForgotPasswordData) => {
    return await forgotPasswordMutation.mutateAsync(data);
  }, [forgotPasswordMutation]);

  const resetPassword = useCallback(async (data: ResetPasswordData) => {
    return await resetPasswordMutation.mutateAsync(data);
  }, [resetPasswordMutation]);

  const resendVerificationEmail = useCallback(async () => {
    return await resendVerificationEmailMutation.mutateAsync();
  }, [resendVerificationEmailMutation]);

  const verifyEmail = useCallback(async (userId: string, hash: string) => {
    return await verifyEmailMutation.mutateAsync({ userId, hash });
  }, [verifyEmailMutation]);

  const value = {
    user,
    isLoading: isLoading || loginMutation.isPending || registerMutation.isPending,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    resendVerificationEmail,
    verifyEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};